// First-touch attribution capture from URL parameters.
//
// A user landing on vrs.racing via a Meta / Google / TikTok / affiliate ad
// carries UTM parameters in the landing URL (utm_source, utm_medium,
// utm_campaign, utm_content, utm_term) plus optional click IDs (fbclid, gclid,
// ttclid, msclkid). We capture the FIRST-touch set the first time a visitor
// lands and stash it in localStorage for 30 days. Every subsequent order,
// subscription, and conversion event can then attach the attribution set that
// originally sourced the visitor — independent of pixel state, consent, or
// third-party cookies.
//
// Why "first touch" vs last touch: for high-consideration purchases (hardware
// €1,098, subscription $10/mo) the marketing-attribution research literature
// consistently shows the FIRST touch is the higher-signal attribution moment.
// Last-touch would credit a branded search or direct type-in that only happens
// because the earlier ad seeded the brand. First touch fixes that.
//
// TTL 30d: cookie-attribution windows for Meta / Google are 7-day / 30-day
// click. 30d matches Meta's max click-window and outlives typical hardware
// research cycles (which run ~2 weeks).

const STORAGE_KEY = 'vrs_attribution'
const TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
const CLICK_ID_FIELDS = ['fbclid', 'gclid', 'ttclid', 'msclkid'] // Meta / Google / TikTok / Bing

// Read attribution params from the current URL. Returns undefined if none
// present — we do NOT overwrite an existing first-touch with an empty set.
function readAttributionFromUrl() {
  if (typeof window === 'undefined') return undefined
  const params = new URLSearchParams(window.location.search)
  const out = {}
  for (const k of UTM_FIELDS) {
    const v = params.get(k)
    if (v) out[k] = v
  }
  for (const k of CLICK_ID_FIELDS) {
    const v = params.get(k)
    if (v) out[k] = v
  }
  if (Object.keys(out).length === 0) return undefined
  out.landing_url = window.location.pathname + window.location.search
  out.landing_at = new Date().toISOString()
  const ref = document.referrer
  if (ref) {
    try {
      const host = new URL(ref).hostname
      // Only record the referring HOST (not the full URL) — enough for
      // attribution, avoids storing sensitive query strings.
      if (host && host !== window.location.hostname) out.referrer_host = host
    } catch (_e) { /* ignore malformed referrer */ }
  }
  return out
}

// First-touch write: if nothing stored yet (or the stored entry has aged out),
// overwrite. If a valid first-touch exists, keep it — do NOT clobber.
export function captureAttributionOnLoad() {
  if (typeof window === 'undefined') return
  try {
    const now = Date.now()
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) {
      try {
        const existing = JSON.parse(raw)
        if (existing && existing.stored_at && (now - existing.stored_at) < TTL_MS) {
          return // valid first-touch already stored — respect it
        }
      } catch (_e) { /* stored value corrupt — will overwrite */ }
    }
    const fresh = readAttributionFromUrl()
    if (!fresh) return // no attribution params on this URL — don't overwrite anything
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...fresh, stored_at: now }))
  } catch (_e) {
    // localStorage disabled / quota — silent; attribution is best-effort.
  }
}

// Read whatever first-touch attribution we have. Returns undefined when
// nothing is stored (visitor arrived without UTMs — organic / direct / SEO).
export function readAttribution() {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.stored_at) return undefined
    if ((Date.now() - parsed.stored_at) > TTL_MS) return undefined
    // Return without the internal `stored_at` bookkeeping field.
    const { stored_at, ...rest } = parsed // eslint-disable-line no-unused-vars
    return rest
  } catch (_e) {
    return undefined
  }
}

// Meta match identifiers that live in first-party cookies on `.vrs.racing`:
//   _fbp         Meta browser id (pixel)
//   _fbc         Meta click id, minted by Tag Manager from the first-touch
//                fbclid, consent-gated
//   external_id  our own first-party visitor id, set by Tag Manager
// _fbp / _fbc only exist when the visitor is outside the EU/UK or consented,
// so reading them here never widens what consent allows. Nothing in this file
// ever sets or invents them.
const META_COOKIE_FIELDS = { fbp: '_fbp', fbc: '_fbc', external_id: 'external_id' }

function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return undefined
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]+)'))
  if (!m) return undefined
  try {
    return decodeURIComponent(m[1])
  } catch (_e) {
    return m[1]
  }
}

// Returns { fbp?, fbc?, external_id? } with only the cookies that exist.
export function readMetaCookieIds() {
  const out = {}
  for (const [key, cookie] of Object.entries(META_COOKIE_FIELDS)) {
    const v = readCookie(cookie)
    if (v) out[key] = v
  }
  return out
}

// The attribution set the /signup form sends to the auth server with a new
// account: Meta cookies, first-touch click ids + UTMs, landing_url, and the
// signup's event_id (the same id the `complete_registration` dataLayer event
// carries, so browser and server records of one signup can be joined).
export function buildSignupAttribution(eventId) {
  const attr = readAttribution() || {}
  const out = {}
  if (eventId) out.event_id = eventId
  Object.assign(out, readMetaCookieIds())
  for (const k of [...UTM_FIELDS, ...CLICK_ID_FIELDS, 'landing_url', 'referrer_host']) {
    if (attr[k]) out[k] = attr[k]
  }
  return out
}

// Append the current first-touch UTM set to an outbound URL (e.g. the
// virtualracingschool.appspot.com pricing / billing CTAs). Preserves any
// existing query string. Returns the original url unchanged when no
// attribution is stored.
export function appendAttributionToUrl(url) {
  const attr = readAttribution()
  if (!attr || !url) return url
  try {
    // Only forward the wire-level attribution fields — never the landing_url
    // / landing_at bookkeeping metadata.
    const forwardable = {}
    for (const k of [...UTM_FIELDS, ...CLICK_ID_FIELDS]) {
      if (attr[k]) forwardable[k] = attr[k]
    }
    if (Object.keys(forwardable).length === 0) return url
    // Preserve hash-routed URLs (`.../#/DataPacks?offerTrial`) — append to
    // the hash-side query string, not the top-level one.
    const hashIdx = url.indexOf('#')
    const base = hashIdx >= 0 ? url.slice(0, hashIdx) : url
    const hash = hashIdx >= 0 ? url.slice(hashIdx) : ''
    const sep = hash
      ? (hash.includes('?') ? '&' : '?')
      : (base.includes('?') ? '&' : '?')
    const qs = new URLSearchParams(forwardable).toString()
    return hashIdx >= 0
      ? `${base}${hash}${sep}${qs}`
      : `${base}${sep}${qs}`
  } catch (_e) {
    return url
  }
}

// True for an OAuth / OIDC authorization endpoint (our auth server's
// /oauth/authorize, the legacy Go service's /oauth2/auth). Those URLs carry
// signed state, PKCE and redirect_uri parameters, so nothing is appended to them.
export function isOAuthAuthorizeUrl(url) {
  if (!url) return false
  try {
    const path = new URL(url, 'https://vrs.racing').pathname.toLowerCase()
    return /^\/oauth2?\/(authorize|auth)\/?$/.test(path)
  } catch (_e) {
    return false
  }
}

// Flatten first-touch attribution into a Meta CAPI `custom_data`-compatible
// shape. Meta stores each key as `custom_data.<key>` and it becomes queryable
// via the Events Manager UI + Ads Manager custom-conversion filters.
export function attributionForCustomData() {
  const attr = readAttribution()
  if (!attr) return {}
  const out = {}
  for (const k of [...UTM_FIELDS, ...CLICK_ID_FIELDS]) {
    if (attr[k]) out[k] = attr[k]
  }
  return out
}
